import React, { useState } from "react";
import { Modal, Button, Tab, Table, Icon } from "semantic-ui-react";
import { JsonView } from "react-json-view-lite";
import "react-json-view-lite/dist/index.css";

const JsonPreviewModal = ({ isOpen, onClose, jsonData, context = 'provider', selectedItem = null }) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState('ascending');
    const [showHygienistTooltip, setShowHygienistTooltip] = useState(false);
    const [showCalculationColumns, setShowCalculationColumns] = useState(false);
    const [expandedProviders, setExpandedProviders] = useState(new Set());

    // CSV Export function
    const exportToCSV = (data, filename) => {
        if (!data || data.length === 0) {
            alert('No data to export');
            return;
        }

        // Get all unique headers from the data
        const headers = Object.keys(data[0]);

        // Create CSV content
        const csvContent = [
            headers.join(','), // Header row
            ...data.map(row =>
                headers.map(header => {
                    const value = row[header];
                    // Escape commas and quotes in values
                    if (value === null || value === undefined) return '';
                    const stringValue = String(value);
                    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                        return `"${stringValue.replace(/"/g, '""')}"`;
                    }
                    return stringValue;
                }).join(',')
            )
        ].join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Collect hygienist data from all practices
    const collectHygienistDataFromPractices = (data) => {
        const hygienistData = {
            matchingProviderTreatments: {},
            hygOnWorkedDates: {},
            claimPayCollections: {}
        };

        if (data && data.data && typeof data.data === 'object') {
            Object.entries(data.data).forEach(([practiceName, practiceData]) => {
                if (practiceData.hygienistProductionMatchingProviderTreatments) {
                    hygienistData.matchingProviderTreatments[practiceName] = practiceData.hygienistProductionMatchingProviderTreatments;
                }
                if (practiceData.hygienistProductionHYGOnWorkedDates) {
                    hygienistData.hygOnWorkedDates[practiceName] = practiceData.hygienistProductionHYGOnWorkedDates;
                }
                if (practiceData.hygienistClaimPayCollections) {
                    hygienistData.claimPayCollections[practiceName] = practiceData.hygienistClaimPayCollections;
                }
            });
        }

        return hygienistData;
    };

    const handleSort = (clickedColumn) => {
        if (sortColumn !== clickedColumn) {
            setSortColumn(clickedColumn);
            setSortDirection('ascending');
        } else {
            setSortDirection(sortDirection === 'ascending' ? 'descending' : 'ascending');
        }
    };

    const sortData = (data, column) => {
        if (!column) return data;

        return [...data].sort((a, b) => {
            let aVal = a[column];
            let bVal = b[column];

            // Handle numeric values
            if (column === 'amt' || column === 'PatNum') {
                aVal = parseFloat(aVal) || 0;
                bVal = parseFloat(bVal) || 0;
            }

            if (aVal < bVal) return sortDirection === 'ascending' ? -1 : 1;
            if (aVal > bVal) return sortDirection === 'ascending' ? 1 : -1;
            return 0;
        });
    };

    const renderHygAllocationRecon = () => {
        // This function handles rendering based on context (provider or hygienist)
        if (context === 'provider') {
            // Provider context - show all hygienists for the selected provider
            return (
                <div style={{ padding: '20px' }}>
                    <div style={{
                        backgroundColor: '#e8f5e9',
                        padding: '15px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: '1px solid #4caf50'
                    }}>
                        <h3 style={{ margin: '0 0 10px 0', color: '#2e7d32' }}>
                            <Icon name="user md" /> Provider Context
                        </h3>
                        <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                            Selected Provider: {selectedItem || 'N/A'}
                        </div>
                        <div style={{ marginTop: '10px', color: '#555' }}>
                            This view shows hygienist allocation reconciliation data for the selected provider.
                        </div>
                    </div>
                    
                    {/* Content will be populated based on your requirements */}
                    <div style={{ 
                        padding: '40px', 
                        textAlign: 'center', 
                        backgroundColor: '#f5f5f5',
                        borderRadius: '8px',
                        border: '2px dashed #ccc'
                    }}>
                        <Icon name="clipboard list" size="huge" style={{ color: '#999', marginBottom: '20px' }} />
                        <h4 style={{ color: '#666' }}>Provider Hygienist Allocation Data</h4>
                        <p style={{ color: '#999' }}>
                            Content structure ready. Awaiting data specification for provider view.
                        </p>
                    </div>
                </div>
            );
        } else if (context === 'hygienist') {
            // Hygienist context - show allocation details for the clicked hygienist
            const hygienistCare = jsonData?.price || 0;
            const hygienistBCare = jsonData?.bCare || 0;
            const hygienistABBR = jsonData?.ABBR || '';
            
            // Build allocation map grouped by practice
            const practiceAllocations = {};
            const hygienistData = collectHygienistDataFromPractices(jsonData);
            
            // Allocation status helper (using existing rules)
            const getAllocationStatus = (providerStr) => {
                if (!providerStr) return 'unallocated';

                const providers = providerStr.split('|').map(p => p.trim());
                const docs = providers.filter(p => !p.toUpperCase().startsWith('HYG'));
                const hygs = providers.filter(p => p.toUpperCase().startsWith('HYG'));

                // Check for double allocation: Multiple hygienists with same provider
                if (hygs.length >= 2 && docs.length === 1) return 'double';

                // Valid Allocation: Exactly 1 Doctor must be present
                if (docs.length === 1) return 'valid';

                // Warning: More than one doctor (Ambiguous) or Zero doctors
                return 'warning';
            };

            const getStatusColor = (status) => {
                switch (status) {
                    case 'valid': return { bg: '#d4edda', text: '#155724', border: '#c3e6cb' }; // Green
                    case 'double': return { bg: '#fff3e0', text: '#e65100', border: '#ffcc80' }; // Orange
                    case 'warning': return { bg: '#fff3cd', text: '#856404', border: '#ffeeba' }; // Yellow
                    case 'unallocated': return { bg: '#ffebee', text: '#c62828', border: '#ffcdd2' }; // Red
                    default: return { bg: '#f8f9fa', text: '#343a40', border: '#dee2e6' };
                }
            };
            
            // Process matching provider treatment data and group by practice
            Object.entries(hygienistData.matchingProviderTreatments).forEach(([practiceName, treatments]) => {
                if (Array.isArray(treatments)) {
                    if (!practiceAllocations[practiceName]) {
                        practiceAllocations[practiceName] = {};
                    }
                    
                    treatments.forEach(treatment => {
                        const provider = treatment.Treat_prov || 'Unallocated';
                        const amount = parseFloat(treatment.amt || treatment.Procfee || 0);
                        
                        if (!practiceAllocations[practiceName][provider]) {
                            practiceAllocations[practiceName][provider] = {
                                care: 0,
                                status: getAllocationStatus(provider)
                            };
                        }
                        practiceAllocations[practiceName][provider].care += amount;
                    });
                }
            });

            // Calculate overall totals and status counts
            let totalCare = 0;
            let overallStatusCounts = { valid: 0, double: 0, warning: 0, unallocated: 0 };
            
            Object.values(practiceAllocations).forEach(providers => {
                Object.values(providers).forEach(data => {
                    totalCare += data.care;
                    overallStatusCounts[data.status] = (overallStatusCounts[data.status] || 0) + 1;
                });
            });

            return (
                <div style={{ padding: '16px' }}>
                    {/* Header */}
                    <div style={{
                        backgroundColor: '#f8f9fa',
                        padding: '10px 14px',
                        borderRadius: '6px',
                        marginBottom: '16px',
                        border: '1px solid #dee2e6'
                    }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '3px' }}>Hygienist</div>
                        <div style={{ fontSize: '15px', fontWeight: '600', color: '#212529' }}>
                            {selectedItem} ({hygienistABBR})
                        </div>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '6px' }}>
                            Total Care: <strong>${hygienistCare.toFixed(2)}</strong> | Billable: <strong>${hygienistBCare.toFixed(2)}</strong>
                        </div>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                            <strong>{Object.keys(practiceAllocations).length}</strong> {Object.keys(practiceAllocations).length === 1 ? 'Practice' : 'Practices'}
                        </div>
                    </div>

                    {/* Overall Status Summary */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                        {overallStatusCounts.valid > 0 && (
                            <div style={{
                                padding: '4px 10px',
                                backgroundColor: '#d4edda',
                                border: '1px solid #c3e6cb',
                                borderRadius: '4px',
                                fontSize: '12px',
                                color: '#155724',
                                fontWeight: '600'
                            }}>
                                ✓ Valid: {overallStatusCounts.valid}
                            </div>
                        )}
                        {overallStatusCounts.double > 0 && (
                            <div style={{
                                padding: '4px 10px',
                                backgroundColor: '#fff3e0',
                                border: '1px solid #ffcc80',
                                borderRadius: '4px',
                                fontSize: '12px',
                                color: '#e65100',
                                fontWeight: '600'
                            }}>
                                ⚡ Double: {overallStatusCounts.double}
                            </div>
                        )}
                        {overallStatusCounts.warning > 0 && (
                            <div style={{
                                padding: '4px 10px',
                                backgroundColor: '#fff3cd',
                                border: '1px solid #ffeeba',
                                borderRadius: '4px',
                                fontSize: '12px',
                                color: '#856404',
                                fontWeight: '600'
                            }}>
                                ⚠ Warning: {overallStatusCounts.warning}
                            </div>
                        )}
                        {overallStatusCounts.unallocated > 0 && (
                            <div style={{
                                padding: '4px 10px',
                                backgroundColor: '#ffebee',
                                border: '1px solid #ffcdd2',
                                borderRadius: '4px',
                                fontSize: '12px',
                                color: '#c62828',
                                fontWeight: '600'
                            }}>
                                ✗ Unallocated: {overallStatusCounts.unallocated}
                            </div>
                        )}
                    </div>
                    
                    {/* Practice Tables */}
                    {Object.keys(practiceAllocations).length > 0 ? (
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(2, 1fr)', 
                            gap: '16px'
                        }}>
                            {Object.entries(practiceAllocations)
                                .sort((a, b) => {
                                    const totalA = Object.values(a[1]).reduce((sum, d) => sum + d.care, 0);
                                    const totalB = Object.values(b[1]).reduce((sum, d) => sum + d.care, 0);
                                    return totalB - totalA;
                                })
                                .map(([practiceName, providers]) => {
                                    const allocationList = Object.entries(providers)
                                        .map(([provider, data]) => ({ provider, care: data.care, status: data.status }))
                                        .sort((a, b) => b.care - a.care);
                                    
                                    const practiceTotal = allocationList.reduce((sum, item) => sum + item.care, 0);

                                    return (
                                        <div key={practiceName} style={{ 
                                            border: '1px solid #dee2e6',
                                            borderRadius: '6px',
                                            overflow: 'hidden'
                                        }}>
                                            {/* Practice Header */}
                                            <div style={{
                                                backgroundColor: '#4a90e2',
                                                padding: '8px 12px',
                                                borderBottom: '1px solid #3a7bc8'
                                            }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <div>
                                                        <Icon name="building" style={{ marginRight: '6px', color: 'white' }} />
                                                        <strong style={{ fontSize: '13px', color: 'white' }}>{practiceName}</strong>
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: 'white' }}>
                                                        Total: <strong>${practiceTotal.toFixed(2)}</strong>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Practice Allocation Table */}
                                            <Table compact celled size="small" style={{ margin: 0, border: 'none' }}>
                                                <Table.Header>
                                                    <Table.Row>
                                                        <Table.HeaderCell 
                                                            textAlign="center" 
                                                            style={{ 
                                                                fontSize: '12px', 
                                                                fontWeight: '700',
                                                                backgroundColor: '#4a90e2',
                                                                color: 'white',
                                                                width: '80px'
                                                            }}
                                                        >
                                                            Status
                                                        </Table.HeaderCell>
                                                        <Table.HeaderCell 
                                                            style={{ 
                                                                fontSize: '12px', 
                                                                fontWeight: '700',
                                                                backgroundColor: '#4a90e2',
                                                                color: 'white'
                                                            }}
                                                        >
                                                            Allocated Provider
                                                        </Table.HeaderCell>
                                                        <Table.HeaderCell 
                                                            textAlign="right" 
                                                            style={{ 
                                                                fontSize: '12px', 
                                                                fontWeight: '700',
                                                                backgroundColor: '#4a90e2',
                                                                color: 'white',
                                                                width: '120px'
                                                            }}
                                                        >
                                                            Care Amount
                                                        </Table.HeaderCell>
                                                    </Table.Row>
                                                </Table.Header>
                                                <Table.Body>
                                                    {allocationList.map((item, idx) => {
                                                        const colors = getStatusColor(item.status);
                                                        return (
                                                            <Table.Row 
                                                                key={idx}
                                                                style={{ 
                                                                    backgroundColor: colors.bg
                                                                }}
                                                            >
                                                                <Table.Cell style={{ fontSize: '11px', padding: '8px', width: '80px', textAlign: 'center' }}>
                                                                    <div style={{
                                                                        display: 'inline-block',
                                                                        width: '10px',
                                                                        height: '10px',
                                                                        borderRadius: '50%',
                                                                        backgroundColor: colors.text,
                                                                        marginRight: '6px'
                                                                    }}></div>
                                                                    <span style={{ color: colors.text, fontWeight: '600' }}>
                                                                        {item.status === 'valid' ? '✓' : 
                                                                         item.status === 'double' ? '⚡' :
                                                                         item.status === 'unallocated' ? '✗' : '⚠'}
                                                                    </span>
                                                                </Table.Cell>
                                                                <Table.Cell style={{ fontSize: '13px' }}>
                                                                    <Icon name="user doctor" style={{ marginRight: '6px' }} />
                                                                    {item.provider}
                                                                </Table.Cell>
                                                                <Table.Cell textAlign="right" style={{ fontSize: '13px', fontWeight: '600' }}>
                                                                    ${item.care.toFixed(2)}
                                                                </Table.Cell>
                                                            </Table.Row>
                                                        );
                                                    })}
                                                </Table.Body>
                                            </Table>
                                        </div>
                                    );
                                })}
                        </div>
                    ) : (
                        <div style={{ 
                            padding: '20px', 
                            textAlign: 'center', 
                            color: '#6c757d',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '6px',
                            fontSize: '13px'
                        }}>
                            No allocation data available
                        </div>
                    )}
                </div>
            );
        } else {
            // Default view when no context is provided
            return (
                <div style={{ padding: '20px' }}>
                    <div style={{
                        backgroundColor: '#fff3e0',
                        padding: '15px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: '1px solid #ff9800'
                    }}>
                        <h3 style={{ margin: '0 0 10px 0', color: '#e65100' }}>
                            <Icon name="info circle" /> No Context Selected
                        </h3>
                        <div style={{ color: '#555' }}>
                            Please select a provider or click on a hygienist to view allocation reconciliation data.
                        </div>
                    </div>
                </div>
            );
        }
    };

    const renderHygienistTable = (dataByPractice, title) => {
        // Flatten data from all practices into a single array
        const allRecords = [];
        Object.entries(dataByPractice).forEach(([practiceName, records]) => {
            if (Array.isArray(records)) {
                records.forEach(record => {
                    allRecords.push({ practiceName, ...record });
                });
            }
        });

        if (allRecords.length === 0) {
            return <div style={{ padding: '20px', textAlign: 'center' }}>No data available</div>;
        }

        const sortedData = sortData(allRecords, sortColumn);

        // Calculate totals - check both amt and Procfee fields
        const totalAmt = allRecords.reduce((sum, record) => {
            const amount = parseFloat(record.amt || record.Procfee || 0);
            return sum + amount;
        }, 0);
        const totalRecords = allRecords.length;

        // Calculate totals for calculation columns
        const totalBCare = allRecords.reduce((sum, record) => sum + (parseFloat(record.bCare) || 0), 0);
        const totalWriteOff = allRecords.reduce((sum, record) => sum + (parseFloat(record.writeOff) || 0), 0);
        const totalAdjustment = allRecords.reduce((sum, record) => sum + (parseFloat(record.adjustment) || 0), 0);

        // Calculate totals by source
        const collectionAmt = allRecords.reduce((sum, record) => {
            if (record.Treat_prov_source === 'collection') {
                const amount = parseFloat(record.amt || record.Procfee || 0);
                return sum + amount;
            }
            return sum;
        }, 0);
        const collectionCount = allRecords.filter(r => r.Treat_prov_source === 'collection').length;

        const productionAmt = allRecords.reduce((sum, record) => {
            if (record.Treat_prov_source === 'production') {
                const amount = parseFloat(record.amt || record.Procfee || 0);
                return sum + amount;
            }
            return sum;
        }, 0);
        const productionCount = allRecords.filter(r => r.Treat_prov_source === 'production').length;

        // Check allocation status helper
        const getAllocationStatus = (providerStr) => {
            if (!providerStr) return 'unallocated';

            const providers = providerStr.split('|').map(p => p.trim());
            const docs = providers.filter(p => !p.toUpperCase().startsWith('HYG'));

            // Valid Allocation: Exactly 1 Doctor must be present
            // It doesn't matter how many hygienists are there, as long as there is one single doctor to allocate to.
            if (docs.length === 1) return 'valid';

            // Warning: 
            // - More than one doctor (Ambiguous allocation)
            // - Zero doctors (No doctor to allocate to)
            return 'warning';
        };

        const getStatusColor = (status) => {
            switch (status) {
                case 'valid': return { bg: '#d4edda', text: '#155724', border: '#c3e6cb' }; // Green
                case 'warning': return { bg: '#fff3cd', text: '#856404', border: '#ffeeba' }; // Yellow
                case 'unallocated': return { bg: '#ffebee', text: '#c62828', border: '#ffcdd2' }; // Red
                default: return { bg: '#f8f9fa', text: '#343a40', border: '#dee2e6' };
            }
        };

        // Calculate allocation sums
        const allocationSums = allRecords.reduce((acc, record) => {
            let provider = record.Treat_prov || 'Unallocated';

            if (provider !== 'Unallocated') {
                // Normalize: Split by | or , (to handle legacy/cache), trim, SORT, and join
                // This merges "A | B" and "B | A" into a single key "A | B"
                provider = provider.split(/\||,/).map(p => p.trim()).sort().join(' | ');
            }

            acc[provider] = (acc[provider] || 0) + parseFloat(record.amt || record.Procfee || 0);
            return acc;
        }, {});

        // Calculate unique hygienists
        const uniqueHygienists = [...new Set(allRecords.map(r => r.Provider).filter(Boolean))];

        const handleExportHygienist = () => {
            // ... existing export logic ...
            const exportData = allRecords.map(record => ({
                Practice: record.practiceName || record.Practice,
                Category: record.Category || 'N/A',
                'Patient #': record.PatNum,
                'Proc Date': record.ProcDate,
                Provider: record.Provider,
                'Treating Provider': record.Treat_prov || '',
                'Scheduled Provider': record.Scheduled_prov || '',
                'Cash Split Provider': record.Cash_Split_prov || '',
                'Claim Pay Provider': record.Claim_pay_prov || '',
                'Pay Provider': record.Pay_prov || '',
                'Production Provider': record.Production_prov || '',
                'Source': record.Treat_prov_source || '',
                Amount: parseFloat(record.amt || record.Procfee || 0).toFixed(2),
                'bCare': record.bCare ? parseFloat(record.bCare).toFixed(2) : '0.00',
                'Write Off': record.writeOff ? parseFloat(record.writeOff).toFixed(2) : '0.00',
                'Adjustment': record.adjustment ? parseFloat(record.adjustment).toFixed(2) : '0.00',
                'Prod Adj %': record.prodAdjWo ? parseFloat(record.prodAdjWo).toFixed(2) : '90.00',
                'Proc Code': record.ProcCode || ''
            }));
            const timestamp = new Date().toISOString().split('T')[0];
            exportToCSV(exportData, `hygienist-production-${timestamp}.csv`);
        };

        return (
            <div style={{ padding: '10px' }}>
                {/* Allocation Summary Labels */}
                <div style={{
                    marginBottom: '10px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px'
                }}>
                    {Object.entries(allocationSums).sort((a, b) => b[1] - a[1]).map(([provider, amount]) => {
                        const status = provider === 'Unallocated' ? 'unallocated' : getAllocationStatus(provider);
                        const colors = getStatusColor(status);
                        return (
                            <div key={provider} style={{
                                padding: '4px 8px',
                                backgroundColor: colors.bg,
                                color: colors.text,
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                border: `1px solid ${colors.border}`
                            }}>
                                {provider}: ${amount.toFixed(2)}
                            </div>
                        );
                    })}
                </div>

                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px',
                    padding: '10px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '4px'
                }}>
                    <h4 style={{ margin: 0 }}>{title}</h4>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <div style={{ fontSize: '14px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <div style={{ fontWeight: 'bold', fontSize: '16px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <span>Total Records: {totalRecords} | Total Amount: ${totalAmt.toFixed(2)}</span>
                                <span style={{ fontSize: '14px', fontWeight: 'normal' }}>|</span>
                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                    <span
                                        onClick={() => setShowHygienistTooltip(!showHygienistTooltip)}
                                        style={{
                                            cursor: 'pointer',
                                            padding: '4px 8px',
                                            backgroundColor: '#6cb043',
                                            color: 'white',
                                            borderRadius: '4px',
                                            fontSize: '14px',
                                            fontWeight: 'bold'
                                        }}
                                        title="Click to see hygienist list"
                                    >
                                        {uniqueHygienists.length} Unique Hygienist{uniqueHygienists.length !== 1 ? 's' : ''}
                                    </span>
                                    {showHygienistTooltip && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '100%',
                                            right: '0',
                                            marginTop: '5px',
                                            backgroundColor: 'white',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            padding: '10px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                            zIndex: 1000,
                                            minWidth: '200px',
                                            maxHeight: '300px',
                                            overflowY: 'auto'
                                        }}>
                                            <div style={{
                                                fontWeight: 'bold',
                                                marginBottom: '8px',
                                                borderBottom: '1px solid #eee',
                                                paddingBottom: '5px',
                                                color: '#333'
                                            }}>
                                                Hygienists ({uniqueHygienists.length})
                                            </div>
                                            {uniqueHygienists.sort().map((hyg, idx) => (
                                                <div key={idx} style={{
                                                    padding: '4px 0',
                                                    color: '#166b83',
                                                    fontSize: '13px'
                                                }}>
                                                    • {hyg}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div style={{ fontSize: '13px', display: 'flex', gap: '20px' }}>
                                <span style={{
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    backgroundColor: '#e8f4ff',
                                    color: '#558ce7'
                                }}>
                                    <strong>Collection:</strong> {collectionCount} records | ${collectionAmt.toFixed(2)}
                                </span>
                                <span style={{
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    backgroundColor: '#f0faff',
                                    color: '#1a89a7'
                                }}>
                                    <strong>Production:</strong> {productionCount} records | ${productionAmt.toFixed(2)}
                                </span>
                            </div>
                        </div>
                        <Button
                            color="green"
                            size="small"
                            onClick={handleExportHygienist}
                            icon
                            labelPosition="left"
                        >
                            <Icon name="download" />
                            Export CSV
                        </Button>
                    </div>
                </div>
                <div>
                    <Table celled sortable striped compact>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'practiceName' ? sortDirection : null}
                                    onClick={() => handleSort('practiceName')}
                                >
                                    Practice
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'Category' ? sortDirection : null}
                                    onClick={() => handleSort('Category')}
                                >
                                    Category
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'PatNum' ? sortDirection : null}
                                    onClick={() => handleSort('PatNum')}
                                >
                                    Patient #
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'ProcDate' ? sortDirection : null}
                                    onClick={() => handleSort('ProcDate')}
                                >
                                    Proc Date
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'Provider' ? sortDirection : null}
                                    onClick={() => handleSort('Provider')}
                                >
                                    Provider
                                </Table.HeaderCell>

                                <Table.HeaderCell
                                    sorted={sortColumn === 'Treat_prov' ? sortDirection : null}
                                    onClick={() => handleSort('Treat_prov')}
                                >
                                    Treating Provider
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'Scheduled_prov' ? sortDirection : null}
                                    onClick={() => handleSort('Scheduled_prov')}
                                >
                                    Scheduled Provider
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'Cash_Split_prov' ? sortDirection : null}
                                    onClick={() => handleSort('Cash_Split_prov')}
                                >
                                    Cash Split Prov
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'Claim_pay_prov' ? sortDirection : null}
                                    onClick={() => handleSort('Claim_pay_prov')}
                                >
                                    Claim Pay Prov
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'Pay_prov' ? sortDirection : null}
                                    onClick={() => handleSort('Pay_prov')}
                                >
                                    Pay Prov
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'Production_prov' ? sortDirection : null}
                                    onClick={() => handleSort('Production_prov')}
                                >
                                    Production Prov
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'Treat_prov_source' ? sortDirection : null}
                                    onClick={() => handleSort('Treat_prov_source')}
                                >
                                    Source
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'amt' ? sortDirection : null}
                                    onClick={() => handleSort('amt')}
                                >
                                    Amount
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'bCare' ? sortDirection : null}
                                    onClick={() => handleSort('bCare')}
                                >
                                    bCare
                                </Table.HeaderCell>
                                {showCalculationColumns && (
                                    <>
                                        <Table.HeaderCell
                                            sorted={sortColumn === 'writeOff' ? sortDirection : null}
                                            onClick={() => handleSort('writeOff')}
                                        >
                                            Write Off
                                        </Table.HeaderCell>
                                        <Table.HeaderCell
                                            sorted={sortColumn === 'adjustment' ? sortDirection : null}
                                            onClick={() => handleSort('adjustment')}
                                        >
                                            Adjustment
                                        </Table.HeaderCell>
                                        <Table.HeaderCell
                                            sorted={sortColumn === 'prodAdjWo' ? sortDirection : null}
                                            onClick={() => handleSort('prodAdjWo')}
                                        >
                                            Prod Adj %
                                        </Table.HeaderCell>
                                    </>
                                )}
                                <Table.HeaderCell
                                    sorted={sortColumn === 'ProcCode' ? sortDirection : null}
                                    onClick={() => handleSort('ProcCode')}
                                >
                                    Proc Code
                                </Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {sortedData.map((record, index) => {
                                // Try to get amount from either amt or Procfee field
                                const amount = parseFloat(record.amt || record.Procfee || 0);
                                const isUnallocated = !record.Treat_prov;

                                return (
                                    <Table.Row key={index}>
                                        <Table.Cell>{record.practiceName || record.Practice}</Table.Cell>
                                        <Table.Cell>{record.Category || 'N/A'}</Table.Cell>
                                        <Table.Cell>{record.PatNum}</Table.Cell>
                                        <Table.Cell>{record.ProcDate}</Table.Cell>
                                        <Table.Cell>{record.Provider}</Table.Cell>

                                        <Table.Cell>
                                            {record.Treat_prov ? (() => {
                                                const status = getAllocationStatus(record.Treat_prov);
                                                const colors = getStatusColor(status);

                                                return (
                                                    <span style={{
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '11px',
                                                        fontWeight: 'bold',
                                                        backgroundColor: colors.bg,
                                                        color: colors.text,
                                                        border: `1px solid ${colors.border}`
                                                    }}>
                                                        {record.Treat_prov}
                                                    </span>
                                                );
                                            })() : (
                                                <span style={{ color: '#c62828', fontWeight: 'bold' }}>Unallocated</span>
                                            )}
                                        </Table.Cell>
                                        <Table.Cell>{record.Scheduled_prov || ''}</Table.Cell>
                                        <Table.Cell>{record.Cash_Split_prov || ''}</Table.Cell>
                                        <Table.Cell>{record.Claim_pay_prov || ''}</Table.Cell>
                                        <Table.Cell>{record.Pay_prov || ''}</Table.Cell>
                                        <Table.Cell>{record.Production_prov || ''}</Table.Cell>
                                        <Table.Cell>
                                            {record.Treat_prov_source ? (
                                                <span style={{
                                                    padding: '4px 8px',
                                                    borderRadius: '4px',
                                                    fontSize: '11px',
                                                    fontWeight: 'bold',
                                                    backgroundColor: record.Treat_prov_source === 'collection' ? '#e8f4ff' : '#f0faff',
                                                    color: record.Treat_prov_source === 'collection' ? '#558ce7' : '#1a89a7',
                                                    textTransform: 'capitalize'
                                                }}>
                                                    {record.Treat_prov_source}
                                                </span>
                                            ) : ''}
                                        </Table.Cell>
                                        <Table.Cell>${amount.toFixed(2)}</Table.Cell>
                                        <Table.Cell>${(record.bCare || 0).toFixed(2)}</Table.Cell>
                                        {showCalculationColumns && (
                                            <>
                                                <Table.Cell>${(record.writeOff || 0).toFixed(2)}</Table.Cell>
                                                <Table.Cell>${(record.adjustment || 0).toFixed(2)}</Table.Cell>
                                                <Table.Cell>{(record.prodAdjWo || 90).toFixed(2)}%</Table.Cell>
                                            </>
                                        )}
                                        <Table.Cell>{record.ProcCode || ''}</Table.Cell>
                                    </Table.Row>
                                );
                            })}
                        </Table.Body>
                        <Table.Footer>
                            <Table.Row>
                                <Table.HeaderCell colSpan="12">TOTAL ({totalRecords} records):</Table.HeaderCell>
                                <Table.HeaderCell>${totalAmt.toFixed(2)}</Table.HeaderCell>
                                <Table.HeaderCell>${totalBCare.toFixed(2)}</Table.HeaderCell>
                                {showCalculationColumns && (
                                    <>
                                        <Table.HeaderCell>${totalWriteOff.toFixed(2)}</Table.HeaderCell>
                                        <Table.HeaderCell>${totalAdjustment.toFixed(2)}</Table.HeaderCell>
                                        <Table.HeaderCell>-</Table.HeaderCell>
                                    </>
                                )}
                                <Table.HeaderCell></Table.HeaderCell>
                            </Table.Row>
                        </Table.Footer>
                    </Table>
                </div>
            </div>
        );
    };

    const [categoryFilter, setCategoryFilter] = React.useState(null);

    const getCategoryColor = (category) => {
        switch (category) {
            case 'Collections':
                return '#fff3cd'; // Light yellow
            case 'PT Income':
                return '#d1ecf1'; // Light cyan
            case 'Claim':
                return '#d4edda'; // Light green
            case 'Pay':
                return '#e2e6ea'; // Light grey
            default:
                return 'transparent';
        }
    };

    const renderClaimPayCollectionsTable = (dataByPractice, title) => {
        // Flatten data from all practices into a single array
        const allRecords = [];
        Object.entries(dataByPractice).forEach(([practiceName, records]) => {
            if (Array.isArray(records)) {
                records.forEach(record => {
                    allRecords.push({ practiceName, ...record });
                });
            }
        });

        // Filter by category if selected
        const filteredRecords = categoryFilter
            ? allRecords.filter(record => record.Category === categoryFilter)
            : allRecords;

        if (allRecords.length === 0) {
            return <div style={{ padding: '20px', textAlign: 'center' }}>No data available</div>;
        }

        const sortedData = sortData(filteredRecords, sortColumn);

        // Calculate total amount
        const totalAmt = filteredRecords.reduce((sum, record) => {
            const amount = parseFloat(record.Amount || 0);
            return sum + amount;
        }, 0);
        const totalRecords = filteredRecords.length;

        // Get unique categories for filter buttons
        const categoriesSet = new Set(allRecords.map(r => r.Category).filter(Boolean));
        const categories = Array.from(categoriesSet);
        const filteredCategories = categories.filter(cat => ['Collections', 'PT Income', 'Claim', 'Pay'].includes(cat));

        const handleExportCollections = () => {
            const exportData = filteredRecords.map(record => ({
                Practice: record.practiceName || record.Practice,
                'Patient #': record.PatNum,
                'Dr Name': record.DrName || 'N/A',
                Abbreviation: record.Abbr || record.ABBR || 'N/A',
                Category: record.Category || 'N/A',
                'Proc Date': record.ProcDate,
                'Entry Date': record.EntryDate,
                'OD User': record.OD_user || 'N/A',
                Amount: parseFloat(record.Amount || 0).toFixed(2)
            }));
            const timestamp = new Date().toISOString().split('T')[0];
            const categoryLabel = categoryFilter ? `-${categoryFilter.replace(/\s+/g, '-')}` : '';
            exportToCSV(exportData, `claim-pay-collections${categoryLabel}-${timestamp}.csv`);
        };

        return (
            <div style={{ padding: '10px' }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px',
                    padding: '10px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '4px'
                }}>
                    <h4 style={{ margin: 0 }}>{title}</h4>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                            Total Records: {totalRecords} | Total Amount: ${totalAmt.toFixed(2)}
                        </div>
                        <Button
                            color="green"
                            size="small"
                            onClick={handleExportCollections}
                            icon
                            labelPosition="left"
                        >
                            <Icon name="download" />
                            Export CSV
                        </Button>
                    </div>
                </div>

                {/* Category Filter Buttons */}
                <div style={{
                    marginBottom: '15px',
                    padding: '10px',
                    backgroundColor: '#f0f0f0',
                    borderRadius: '4px',
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center'
                }}>
                    <span style={{ fontWeight: 'bold', marginRight: '10px' }}>Filter:</span>
                    <button
                        onClick={() => setCategoryFilter(null)}
                        style={{
                            padding: '6px 12px',
                            backgroundColor: categoryFilter === null ? '#007bff' : '#e0e0e0',
                            color: categoryFilter === null ? 'white' : 'black',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: categoryFilter === null ? 'bold' : 'normal'
                        }}
                    >
                        All
                    </button>
                    {filteredCategories.map(category => (
                        <button
                            key={category}
                            onClick={() => setCategoryFilter(category)}
                            style={{
                                padding: '6px 12px',
                                backgroundColor: categoryFilter === category ? getCategoryColor(category) : '#e0e0e0',
                                border: categoryFilter === category ? '2px solid #333' : '1px solid #ccc',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontWeight: categoryFilter === category ? 'bold' : 'normal'
                            }}
                        >
                            {category}
                        </button>
                    ))}
                </div>

                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    <Table celled sortable striped compact>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'practiceName' ? sortDirection : null}
                                    onClick={() => handleSort('practiceName')}
                                >
                                    Practice
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'PatNum' ? sortDirection : null}
                                    onClick={() => handleSort('PatNum')}
                                >
                                    Patient #
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'DrName' ? sortDirection : null}
                                    onClick={() => handleSort('DrName')}
                                >
                                    Dr Name
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'Abbr' ? sortDirection : null}
                                    onClick={() => handleSort('Abbr')}
                                >
                                    Abbreviation
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'Category' ? sortDirection : null}
                                    onClick={() => handleSort('Category')}
                                >
                                    Category
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'ProcDate' ? sortDirection : null}
                                    onClick={() => handleSort('ProcDate')}
                                >
                                    Proc Date
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'EntryDate' ? sortDirection : null}
                                    onClick={() => handleSort('EntryDate')}
                                >
                                    Entry Date
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'OD_user' ? sortDirection : null}
                                    onClick={() => handleSort('OD_user')}
                                >
                                    OD User
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'Amount' ? sortDirection : null}
                                    onClick={() => handleSort('Amount')}
                                    textAlign="right"
                                >
                                    Amount
                                </Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {sortedData.map((record, index) => {
                                const amount = parseFloat(record.Amount || 0);
                                const rowBackgroundColor = getCategoryColor(record.Category);
                                return (
                                    <Table.Row key={index} style={{ backgroundColor: rowBackgroundColor }}>
                                        <Table.Cell>{record.practiceName || record.Practice}</Table.Cell>
                                        <Table.Cell>{record.PatNum}</Table.Cell>
                                        <Table.Cell>{record.DrName || 'N/A'}</Table.Cell>
                                        <Table.Cell>{record.Abbr || record.ABBR || 'N/A'}</Table.Cell>
                                        <Table.Cell>{record.Category || 'N/A'}</Table.Cell>
                                        <Table.Cell>{record.ProcDate}</Table.Cell>
                                        <Table.Cell>{record.EntryDate}</Table.Cell>
                                        <Table.Cell>{record.OD_user || 'N/A'}</Table.Cell>
                                        <Table.Cell textAlign="right">
                                            ${amount.toFixed(2)}
                                        </Table.Cell>
                                    </Table.Row>
                                );
                            })}
                        </Table.Body>
                        <Table.Footer>
                            <Table.Row style={{ backgroundColor: '#f2f2f2' }}>
                                <Table.HeaderCell colSpan="8" textAlign="right" style={{ fontWeight: 'bold', fontSize: '14px' }}>
                                    TOTAL ({totalRecords} records):
                                </Table.HeaderCell>
                                <Table.HeaderCell textAlign="right" style={{ fontWeight: 'bold', fontSize: '14px', backgroundColor: '#e8f4f8' }}>
                                    ${totalAmt.toFixed(2)}
                                </Table.HeaderCell>
                            </Table.Row>
                        </Table.Footer>
                    </Table>
                </div>
            </div>
        );
    };

    const hygienistData = collectHygienistDataFromPractices(jsonData);
    const hasHygienistData = Object.keys(hygienistData.matchingProviderTreatments).length > 0 ||
        Object.keys(hygienistData.claimPayCollections).length > 0 ||
        Object.keys(hygienistData.hygOnWorkedDates).length > 0;

    const panes = [];

    if (hasHygienistData) {
        panes.push({
            menuItem: 'Hygienist Matching Provider Treatments',
            render: () => (
                <Tab.Pane>
                    {renderHygienistTable(hygienistData.matchingProviderTreatments, 'Hygienist Production Matching Provider Treatment Combinations')}
                </Tab.Pane>
            ),
        });
    }

    // Hyg Allocation re con tab
    panes.push({
        menuItem: 'Hyg Allocation Recon',
        render: () => (
            <Tab.Pane>
                {renderHygAllocationRecon()}
            </Tab.Pane>
        ),
    });

    panes.push({
        menuItem: 'Provider Data',
        render: () => (
            <Tab.Pane>
                <JsonView data={jsonData} shouldExpandNode={(level) => level < 1} />
            </Tab.Pane>
        ),
    });

    if (hasHygienistData && Object.keys(hygienistData.claimPayCollections).length > 0) {
        panes.push({
            menuItem: 'Claim Pay Collections',
            render: () => (
                <Tab.Pane>
                    {renderClaimPayCollectionsTable(hygienistData.claimPayCollections, 'Claim Pay Collections')}
                </Tab.Pane>
            ),
        });
    }

    return (
        <Modal open={isOpen} onClose={onClose} size="fullscreen" closeIcon>
            <Modal.Header>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Provider Data Viewer</span>
                    <label style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 'normal' }}>
                        <input
                            type="checkbox"
                            checked={showCalculationColumns}
                            onChange={(e) => setShowCalculationColumns(e.target.checked)}
                            style={{ cursor: 'pointer' }}
                        />
                        <span>Show Calculation Columns (Write Off, Adjustment, Prod Adj %)</span>
                    </label>
                </div>
            </Modal.Header>
            <Modal.Content scrolling style={{ maxHeight: '80vh' }}>
                <Tab
                    panes={panes}
                    activeIndex={activeIndex}
                    onTabChange={(e, { activeIndex }) => {
                        setActiveIndex(activeIndex);
                        setSortColumn(null);
                        setSortDirection('ascending');
                    }}
                />
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose} color="red">
                    Close
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default JsonPreviewModal;
